import type { ProductColor } from '@aloft/toolkit-shared';
import { Flex, Typography } from '@aloft/toolkit-sui';

import { GraphTooltipWrapper } from './GraphTooltipWrapper.tsx';

type Row<T, K extends keyof T> = {
  label: string;
  key: K;
  color?: ProductColor | ((p: T) => ProductColor);
  valueFormatter?: (value?: T[K]) => string;
  shouldHide?: (p: T) => boolean;
};

type SimpleGraphTooltipProps<T extends Record<string, string | number | undefined>> = {
  disableAnimations: boolean;
  titleKey: keyof T;
  rows: Row<T, keyof T>[];
};

export const SimpleGraphTooltip = <T extends Record<string, string | number | undefined>>({
  disableAnimations,
  titleKey,
  rows,
  ...tooltipProps
}: SimpleGraphTooltipProps<T>) => (
  <GraphTooltipWrapper disableAnimations={disableAnimations} {...tooltipProps}>
    {({ payload }) => {
      const p = (payload[0] as { payload: T }).payload;
      const title = p[titleKey];

      return (
        <>
          {title && <Typography variant="h3">{title}</Typography>}
          {rows.map(({ label, key, color, valueFormatter, shouldHide }) => {
            if (shouldHide?.(p)) return null;
            const value = p[key];
            const displayValue = value?.toLocaleString() ?? '-';

            return (
              <Flex key={label}>
                <Typography>{label}:&nbsp;</Typography>
                <Typography color={typeof color === 'function' ? color(p) : color}>
                  {valueFormatter ? valueFormatter(value) : displayValue}
                </Typography>
              </Flex>
            );
          })}
        </>
      );
    }}
  </GraphTooltipWrapper>
);
